import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
  Box,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useThemeContext } from '../../../Context/ThemeContext';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

const DeleteConfirmDialog = ({
  open,
  title,
  content,
  onCancel,
  onConfirm,
  zIndex,
  ...props
}) => {
  const { darkMode } = useThemeContext();
  const theme = useTheme();
  const borderBase = 'rgba(79, 172, 254, 0.1)';

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleConfirm = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('DeleteConfirmDialog: handleConfirm called');
    if (onConfirm) {
      console.log('DeleteConfirmDialog: calling onConfirm');
      onConfirm();
    } else {
      console.error('DeleteConfirmDialog: onConfirm is not a function');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        console.log('DeleteConfirmDialog: onClose called with reason:', reason);
        // Only close on backdrop click or escape key, not on button clicks
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          handleCancel();
        }
        // Explicitly prevent closing for any other reason
      }}
      disableEscapeKeyDown={false}
      disableBackdropClick={false}
      {...props}
      slotProps={{
        root: {
          sx: {
            zIndex: zIndex || 1400,
            pointerEvents: 'auto',
            '& > *': {
              pointerEvents: 'auto',
            },
          },
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 1,
          width: 480,
          overflow: 'visible',
          backgroundColor: darkMode ? '#1e1f22' : '#ffffff',
          border: `1px solid ${borderBase}`,
          boxShadow: darkMode
            ? '0 24px 64px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.04)'
            : '0 24px 64px rgba(0,0,0,0.15), inset 0 1px 2px rgba(0,0,0,0.06)',
          position: 'relative',
          zIndex: zIndex || 1400,
          pointerEvents: 'auto',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: darkMode
            ? 'rgba(10, 14, 20, 0.55)'
            : 'rgba(10, 18, 30, 0.35)',
          zIndex: zIndex ? zIndex - 1 : 1399,
          pointerEvents: 'auto',
          // Ensure backdrop doesn't block clicks to dialog content
          '& + *': {
            pointerEvents: 'auto',
          },
        },
        onClick: (e) => {
          // Only close on backdrop click, not on dialog content
          if (e.target === e.currentTarget) {
            handleCancel();
          }
        },
      }}
    >
      <DialogContent
        sx={{
          py: 5,
          px: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            mb: 1,
            background: darkMode ? '#2c2c2e' : '#f7fbff',
            border: `1px solid ${borderBase}`,
            boxShadow: darkMode
              ? 'inset 0 1px 2px rgba(255,255,255,0.04)'
              : 'inset 0 1px 2px rgba(0,0,0,0.06)',
          }}
        >
          <WarningAmberOutlinedIcon
            sx={{ fontSize: '2rem', color: '#ff3126ff' }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: '0.8rem',
            fontWeight: 600,
            letterSpacing: 0.2,
            color: theme.palette.text.primary,
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.95rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            mt: 0.5,
          }}
        >
          {content}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: 400,
            color: theme.palette.text.secondary,
          }}
        >
          This action cannot be undone.
        </Typography>
      </DialogContent>

      <DialogActions 
        sx={{ 
          padding: '8px 24px',
          position: 'relative',
          zIndex: (zIndex || 1400) + 1,
          pointerEvents: 'auto',
        }}
        onClick={(e) => {
          // Prevent clicks in DialogActions from closing the dialog
          e.stopPropagation();
        }}
      >
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('DeleteConfirmDialog: Cancel button clicked');
            handleCancel();
          }}
          variant="text"
          type="button"
          sx={{
            minWidth: 120,
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
            position: 'relative',
            zIndex: (zIndex || 1400) + 2,
            pointerEvents: 'auto !important',
            cursor: 'pointer !important',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent?.stopImmediatePropagation?.();
            console.log('DeleteConfirmDialog: Remove button clicked');
            handleConfirm(e);
          }}
          variant="contained"
          type="button"
          sx={{
            minWidth: 110,
            height: 40,
            borderRadius: 1,
            px: 4,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            fontSize: '0.95rem',
            backgroundImage: 'linear-gradient(90deg, #de1d0fff, #f87f5dff)',
            color: '#fff',
            position: 'relative',
            zIndex: (zIndex || 1400) + 2,
            pointerEvents: 'auto !important',
            cursor: 'pointer !important',
            '&:hover': {
              backgroundImage: 'linear-gradient(90deg, #e61405ff, #de1d0fff)',
              boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
              transform: 'translateY(-1px)',
              cursor: 'pointer !important',
            },
            '&:active': {
              transform: 'translateY(0)',
              boxShadow: 'none',
            },
            transition: 'all .18s ease',
          }}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DeleteConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  content: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  zIndex: PropTypes.number,
};

export default DeleteConfirmDialog;
